/**
 * Рассылки — две legacy-страницы под одним компонентом (маршруты заданы в App.tsx):
 *   /admin/mailing    → htdocs/core/templates/admin_mailing.tpl    (Email + Push + Telegram + MAX одним сообщением)
 *   /admin/email_info → htdocs/core/templates/admin_email_info.tpl (только Email)
 *
 * SMS как канал отключён (решение владельца) — колонки, счётчика и отправки по номерам
 * здесь больше нет.
 *
 * Push, Telegram и MAX — каналы сверх legacy (ни приложения, ни ботов тогда не было).
 * Колонки и счётчики сделаны теми же классами, что Email, но получатель у них другой:
 * не абонент с телефоном или почтой, а УЧАСТОК — с зарегистрированным устройством
 * (rw_push_tokens → push_areas) либо с привязанным чатом (rw_telegram_links →
 * telegram_areas, rw_max_links → max_areas). Поэтому в push_sel[], telegram_sel[] и
 * max_sel[] уходят id участков, а не контакты.
 *
 * Разметка перенесена 1:1: form#form_id.add-form, кнопка «Список рассылки» (.bt.orange),
 * счётчик .margin_null, окно со списком рассылки (общий <Modal> — закрытие по фону/Esc,
 * вёрстка Bootstrap 3 та же), таблица со столбцами
 * .mob_td_areaname/.mob_td_fio/.td_em_tl/.checkbox_modal, строка баланса,
 * textarea#textarea, скрытые поля send_msgs/send_emails и blocker.
 *
 * Защита от повторной отправки — одноразовый токен blocker (GET .../mailing/recipients|emails
 * выдаёт, POST гасит; Handlers\Admin\Mailing::consumeBlocker). Перед отправкой —
 * confirm с явным предупреждением, что сообщения уйдут реальным жителям.
 *
 * WYSIWYG summernote в email-рассылке заменён на <textarea> в той же обёртке — см. notes.
 */

import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { api, ApiError } from '@/api/client'
import { HtmlEditor } from '@/components/HtmlEditor'
import { Modal } from '@/components/Modal'
import { useLegacyCss } from '@/lib/legacyAssets'
import { useApi } from '@/lib/useApi'

/** Абонент рассылки (snt_users + t_area.name). */
interface Recipient {
  id: number
  area_fk: number
  familia: string | null
  imuy: string | null
  otchestvo: string | null
  tel: string | null
  email: string | null
  area_name: string | null
}

/** Ответ GET /api/admin/mailing/recipients (комбинированная рассылка). */
interface RecipientsResponse {
  mailing: Recipient[]
  /** Участки с приложением: только у них показывается галочка Push (Mailing::areasWithPush). */
  push_areas: number[]
  push_devices: number
  /** Участки с привязанным чатом: только у них показывается галочка Telegram. */
  telegram_areas: number[]
  telegram_chats: number
  /** То же для MAX (rw_max_links). */
  max_areas: number[]
  max_chats: number
  blocker: string
}

/** Ответ GET /api/admin/mailing/emails (email-рассылка). */
interface EmailRecipientsResponse {
  emails: Recipient[]
  blocker: string
}

/** Ответ POST /api/admin/mailing/send. */
interface SendResponse {
  ok: string[]
  errors: string[]
  sent_emails: string[]
  /** id участков, которым ушло push-уведомление. */
  sent_push: number[]
  /** id участков, которым ушло сообщение в Telegram. */
  sent_telegram: number[]
  /** id участков, которым ушло сообщение в MAX. */
  sent_max: number[]
  blocker: string
}

/** Ответ POST /api/admin/mailing/emails. */
interface SendEmailsResponse {
  sent: string[]
  blocker: string
  message: string
}

/** Предупреждение перед реальной отправкой (сообщения уходят жителям). */
function confirmSend(what: string): boolean {
  return window.confirm(
    `ВНИМАНИЕ! Сообщение будет ОТПРАВЛЕНО РЕАЛЬНЫМ АБОНЕНТАМ (${what}).\n` +
      'Отменить отправку после подтверждения нельзя. Продолжить?',
  )
}

/** ФИО абонента одной строкой (legacy: {$item.familia} {$item.imuy} {$item.otchestvo}). */
function fio(item: Recipient): string {
  return `${item.familia ?? ''} ${item.imuy ?? ''} ${item.otchestvo ?? ''}`
}

export function AdminMailingPage() {
  const { pathname } = useLocation()
  // legacy: /admin/email_info — только email; /admin/mailing — все каналы
  return pathname.endsWith('email_info') ? <EmailInfo /> : <Mailing />
}

// ─── Комбинированная рассылка (legacy admin_mailing.tpl) ─────────────────────

function Mailing() {
  useLegacyCss('admin', 'newapproach')

  const { data, reload } = useApi<RecipientsResponse>('/api/admin/mailing/recipients')

  const [modalOpen, setModalOpen] = useState(false)
  const [emails, setEmails] = useState<Record<number, boolean>>({})
  const [push, setPush] = useState<Record<number, boolean>>({})
  const [telegram, setTelegram] = useState<Record<number, boolean>>({})
  const [max, setMax] = useState<Record<number, boolean>>({})
  const [message, setMessage] = useState('')
  const [blocker, setBlocker] = useState('')
  const [ok, setOk] = useState<string[]>([])
  const [error, setError] = useState<string[]>([])

  const recipients = data?.mailing ?? []
  // Получатель push — участок с приложением, а не абонент с телефоном/почтой.
  const pushAreas = data?.push_areas ?? []
  const hasPush = (item: Recipient): boolean => pushAreas.includes(item.area_fk)
  // Получатель Telegram — участок с привязанным чатом (муж/жена привязывают каждый свой).
  const telegramAreas = data?.telegram_areas ?? []
  const hasTelegram = (item: Recipient): boolean => telegramAreas.includes(item.area_fk)
  // Получатель MAX — участок с привязанным чатом MAX, ровно как в Telegram.
  const maxAreas = data?.max_areas ?? []
  const hasMax = (item: Recipient): boolean => maxAreas.includes(item.area_fk)

  useEffect(() => {
    if (data !== null) setBlocker(data.blocker)
  }, [data])

  const listKey =
    `${recipients.map((item) => item.id).join(',')}|${pushAreas.join(',')}|${telegramAreas.join(',')}|${maxAreas.join(',')}`

  /**
   * Отметки «выбран» ставим ЗАНОВО, только когда сменился САМ СПИСОК получателей
   * (другой месяц, добавился абонент) — а не на каждое обновление данных.
   *
   * Раньше эффект висел на объекте `data`, и это ломало работу: SWR перезапрашивает
   * данные при возврате на вкладку, приходит НОВЫЙ объект с теми же строками — и выбор,
   * который админ только что расставил руками, затирался «отмечены все».
   */
  useEffect(() => {
    if (data === null) return
    // legacy: все чекбоксы отмечены по умолчанию (checked="checked")
    const emailState: Record<number, boolean> = {}
    const pushState: Record<number, boolean> = {}
    const telegramState: Record<number, boolean> = {}
    const maxState: Record<number, boolean> = {}
    for (const item of data.mailing) {
      if (item.email !== null && item.email !== '') emailState[item.id] = true
      if (data.push_areas.includes(item.area_fk)) pushState[item.id] = true
      if (data.telegram_areas.includes(item.area_fk)) telegramState[item.id] = true
      if (data.max_areas.includes(item.area_fk)) maxState[item.id] = true
    }
    setEmails(emailState)
    setPush(pushState)
    setTelegram(telegramState)
    setMax(maxState)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- намеренно только по составу списка
  }, [listKey])

  const emailCount = Object.values(emails).filter(Boolean).length
  const pushCount = Object.values(push).filter(Boolean).length
  const telegramCount = Object.values(telegram).filter(Boolean).length
  const maxCount = Object.values(max).filter(Boolean).length

  /** legacy select_all('email', '1'|'0') — плюс колонки Push, Telegram и MAX. */
  const selectAll = (value: boolean): void => {
    const emailState: Record<number, boolean> = {}
    const pushState: Record<number, boolean> = {}
    const telegramState: Record<number, boolean> = {}
    const maxState: Record<number, boolean> = {}
    for (const item of recipients) {
      if (item.email !== null && item.email !== '') emailState[item.id] = value
      if (hasPush(item)) pushState[item.id] = value
      if (hasTelegram(item)) telegramState[item.id] = value
      if (hasMax(item)) maxState[item.id] = value
    }
    setEmails(emailState)
    setPush(pushState)
    setTelegram(telegramState)
    setMax(maxState)
  }

  /** legacy send_msgs. */
  const send = async (): Promise<void> => {
    if (
      !confirmSend(
        `${emailCount} Email / ${pushCount} Push / ${telegramCount} Telegram / ${maxCount} MAX`,
      )
    )
      return

    const emailValues = recipients
      .filter((item) => emails[item.id] === true && item.email !== null && item.email !== '')
      .map((item) => item.email as string)
    // В push уходят id участков: устройство привязано к участку (rw_push_tokens.area_fk).
    const pushValues = recipients
      .filter((item) => push[item.id] === true && hasPush(item))
      .map((item) => item.area_fk)
    // В Telegram — тоже id участков: чат привязан к участку (rw_telegram_links.area_fk).
    const telegramValues = recipients
      .filter((item) => telegram[item.id] === true && hasTelegram(item))
      .map((item) => item.area_fk)
    // MAX — тоже id участков (rw_max_links.area_fk).
    const maxValues = recipients
      .filter((item) => max[item.id] === true && hasMax(item))
      .map((item) => item.area_fk)

    try {
      const result = await api.post<SendResponse>('/api/admin/mailing/send', {
        blocker,
        message,
        emails_sel: emailValues,
        push_sel: pushValues,
        telegram_sel: telegramValues,
        max_sel: maxValues,
      })
      setOk(result.ok)
      setError(result.errors)
      setBlocker(result.blocker) // токен одноразовый: получаем новый
      if (result.errors.length === 0) setMessage('')
    } catch (e: unknown) {
      setError([e instanceof ApiError ? e.message : 'Ошибка при отправке'])
      setOk([])
      reload() // токен мог быть погашен — забираем свежий
    }
  }

  return (
    <form
      className="add-form"
      method="POST"
      id="form_id"
      onSubmit={(e) => {
        e.preventDefault()
        void send()
      }}
    >
      <button
        type="button"
        className="bt orange"
        style={{ margin: '0px' }}
        onClick={() => setModalOpen(true)}
      >
        Список рассылки
      </button>

      <p className="margin_null">
        Выбрано: <span id="abon_emails">{emailCount}</span> Email /{' '}
        <span id="abon_push">{pushCount}</span> Push /{' '}
        <span id="abon_telegram">{telegramCount}</span> Telegram /{' '}
        <span id="abon_max">{maxCount}</span> MAX
      </p>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="Список рассылки">
        <a
          style={{ transform: 'scale(0.9)', cursor: 'pointer' }}
          className="bt orange"
          onClick={() => selectAll(true)}
        >
          Выбрать все
        </a>
        <a
          style={{ transform: 'scale(0.9)', cursor: 'pointer' }}
          className="bt red"
          onClick={() => selectAll(false)}
        >
          Убрать все
        </a>
        <button
          style={{ transform: 'scale(0.9)' }}
          type="button"
          className="bt green"
          onClick={() => setModalOpen(false)}
        >
          Сохранить
        </button>

        <table className="table table-hover" style={{ marginTop: '15px' }}>
          <thead>
            <tr>
              <th className="mob_td_areaname">Участок</th>
              <th>ФИО</th>
              <th>Email</th>
              <th>Push</th>
              <th>Telegram</th>
              <th>MAX</th>
            </tr>
          </thead>
          <tbody>
            {recipients
              .filter(
                (item) =>
                  (item.email !== null && item.email !== '') ||
                  hasPush(item) ||
                  hasTelegram(item) ||
                  hasMax(item),
              )
              .map((item) => (
                <tr className="trline" key={item.id}>
                  <td className="mob_td_areaname">{item.area_name}</td>
                  <td className="mob_td_fio">{fio(item)}</td>
                  <td className="td_em_tl">
                    {item.email !== null && item.email !== '' && (
                      <input
                        type="checkbox"
                        name="emails_sel[]"
                        value={item.email}
                        className="emails_sel"
                        checked={emails[item.id] === true}
                        onChange={(e) => setEmails({ ...emails, [item.id]: e.target.checked })}
                      />
                    )}
                  </td>
                  {/* Галочка Push есть только у участков с приложением — у остальных отправлять некуда. */}
                  <td className="td_em_tl">
                    {hasPush(item) && (
                      <input
                        type="checkbox"
                        name="push_sel[]"
                        value={item.area_fk}
                        className="push_sel"
                        checked={push[item.id] === true}
                        onChange={(e) => setPush({ ...push, [item.id]: e.target.checked })}
                      />
                    )}
                  </td>
                  {/* Аналогично Telegram: галочка только у участков с привязанным чатом. */}
                  <td className="td_em_tl">
                    {hasTelegram(item) && (
                      <input
                        type="checkbox"
                        name="telegram_sel[]"
                        value={item.area_fk}
                        className="telegram_sel"
                        checked={telegram[item.id] === true}
                        onChange={(e) => setTelegram({ ...telegram, [item.id]: e.target.checked })}
                      />
                    )}
                  </td>
                  {/* MAX — то же самое: галочка только у участков с привязанным чатом MAX. */}
                  <td className="td_em_tl">
                    {hasMax(item) && (
                      <input
                        type="checkbox"
                        name="max_sel[]"
                        value={item.area_fk}
                        className="max_sel"
                        checked={max[item.id] === true}
                        onChange={(e) => setMax({ ...max, [item.id]: e.target.checked })}
                      />
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </Modal>

      <div className="row" style={{ margin: '0px' }}>
        <div className="col-xs-12" style={{ paddingBottom: '10px', paddingLeft: '0px' }}>
          <div className="informblock">
            {ok.map((message, index) => (
              <p key={index} style={{ color: '#008000' }}>
                {message}
              </p>
            ))}
            {error.map((message, index) => (
              <p key={index} style={{ color: '#ce4844' }}>
                {message}
              </p>
            ))}
          </div>
        </div>
      </div>

      <textarea
        className=""
        id="textarea"
        name="message"
        style={{ height: '300px', width: '100%' }}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
      />
      <div style={{ textAlign: 'center' }}>
        <input type="hidden" name="blocker" value={blocker} />
        <input type="submit" name="send_msgs" className="bt" value="Отправить" />
      </div>
    </form>
  )
}

// ─── Только Email (legacy admin_email_info.tpl) ──────────────────────────────

function EmailInfo() {
  useLegacyCss('admin', 'newapproach')

  const { data, reload } = useApi<EmailRecipientsResponse>('/api/admin/mailing/emails')

  const [modalOpen, setModalOpen] = useState(false)
  const [selected, setSelected] = useState<Record<number, boolean>>({})
  const [title, setTitle] = useState('')
  const [text, setText] = useState('')
  const [blocker, setBlocker] = useState('')
  const [ok, setOk] = useState<string[]>([])
  const [error, setError] = useState<string[]>([])

  const recipients = data?.emails ?? []

  useEffect(() => {
    if (data !== null) setBlocker(data.blocker)
  }, [data])

  const emailsKey = (data?.emails ?? []).map((item) => item.id).join(',')

  /**
   * Отметки «выбран» ставим ЗАНОВО, только когда сменился САМ СПИСОК получателей
   * (другой месяц, добавился абонент) — а не на каждое обновление данных.
   *
   * Раньше эффект висел на объекте `data`, и это ломало работу: SWR перезапрашивает
   * данные при возврате на вкладку, приходит НОВЫЙ объект с теми же строками — и выбор,
   * который админ только что расставил руками, затирался «отмечены все».
   */
  useEffect(() => {
    if (data === null) return
    const state: Record<number, boolean> = {}
    for (const item of data.emails) state[item.id] = true // legacy: все отмечены
    setSelected(state)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- намеренно только по составу списка
  }, [emailsKey])

  const count = Object.values(selected).filter(Boolean).length

  /** legacy select_all('emails', '1'|'0'). */
  const selectAll = (value: boolean): void => {
    const state: Record<number, boolean> = {}
    for (const item of recipients) state[item.id] = value
    setSelected(state)
  }

  /** legacy send_emails. */
  const send = async (): Promise<void> => {
    if (!confirmSend(`${count} Email`)) return

    const addresses = recipients
      .filter((item) => selected[item.id] === true && item.email !== null && item.email !== '')
      .map((item) => item.email as string)

    try {
      const result = await api.post<SendEmailsResponse>('/api/admin/mailing/emails', {
        blocker,
        emails: addresses,
        title,
        text,
      })
      setOk([result.message])
      setError([])
      setBlocker(result.blocker)
      setTitle('')
      setText('')
    } catch (e: unknown) {
      setError([e instanceof ApiError ? e.message : 'Ошибка при отправке'])
      setOk([])
      reload()
    }
  }

  return (
    <form
      className="add-form"
      method="POST"
      id="form_id"
      onSubmit={(e) => {
        e.preventDefault()
        void send()
      }}
    >
      <button type="button" className="bt orange" onClick={() => setModalOpen(true)}>
        Список рассылки
      </button>
      <p className="margin_null">
        Выбрано: <span id="abon">{count}</span> Абон.
      </p>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="Список рассылки">
        <a
          style={{ transform: 'scale(0.9)', cursor: 'pointer' }}
          className="bt orange"
          id="recheck"
          onClick={() => selectAll(true)}
        >
          Выбрать все
        </a>
        <a
          style={{ transform: 'scale(0.9)', cursor: 'pointer' }}
          className="bt red"
          onClick={() => selectAll(false)}
        >
          Убрать все
        </a>
        <button
          style={{ transform: 'scale(0.9)' }}
          type="button"
          className="bt green"
          onClick={() => setModalOpen(false)}
        >
          Сохранить
        </button>

        <table className="table table-hover" style={{ marginTop: '15px' }}>
          <thead>
            <tr>
              <th />
              <th className="mob_td_areaname">Участок</th>
              <th className="mob_td_fio">ФИО</th>
              <th>Email</th>
            </tr>
          </thead>
          <tbody>
            {recipients.map((item) => (
              <tr
                className="RowClass"
                key={item.id}
                // legacy: клик по строке переключает чекбокс
                onClick={(e) => {
                  if ((e.target as HTMLElement).tagName === 'INPUT') return
                  setSelected({ ...selected, [item.id]: selected[item.id] !== true })
                }}
              >
                <td className="checkbox_modal">
                  <input
                    type="checkbox"
                    name="emails[]"
                    value={item.email ?? ''}
                    className="emails_sel"
                    checked={selected[item.id] === true}
                    onChange={(e) => setSelected({ ...selected, [item.id]: e.target.checked })}
                  />
                </td>
                <td className="mob_td_areaname">{item.area_name}</td>
                <td className="mob_td_fio">{fio(item)}</td>
                <td className="td_em_tl">{item.email}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Modal>

      <div className="row" style={{ margin: '0px' }}>
        <div className="col-md-6">
          {ok.map((message, index) => (
            <p key={index} style={{ float: 'left', color: 'green' }}>
              {message}
            </p>
          ))}
          {error.map((message, index) => (
            <span key={index} style={{ color: '#ce4844', float: 'left' }}>
              {message}
            </span>
          ))}
        </div>
      </div>

      <div
        className="form-control news-input"
        style={{ paddingBottom: '6px', display: 'table', marginBottom: '10px' }}
      >
        <input
          type="text"
          name="title"
          style={{ display: 'table-cell', border: 0, outline: 0, width: '100%' }}
          value={title}
          placeholder="Тема письма"
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>

      {/* legacy admin_email_info.tpl: тело письма правится в WYSIWYG (.in_editor).
          В общей рассылке (Mailing) редактора не было — там остаётся textarea. */}
      <HtmlEditor value={text} onChange={setText} />

      <div style={{ textAlign: 'center' }}>
        <input type="hidden" name="blocker" value={blocker} />
        <input type="submit" name="send_emails" className="bt" value="Отправить" />
      </div>
    </form>
  )
}
